import React from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlag } from '@fs/zion-flags'
import { FamilyMembersCard } from '../familyMembersCard/FamilyMembersCard'
import { RelationshipsCard } from './RelationshipsCard'

// REMOVE THIS FILE when `rip_sharedImageViewer_relationshipsCard` is cleaned up: delete it along
// with `familyMembersCard/`, and point the panels straight at RelationshipsCard.

/**
 * Whether the reworked card is the one to render. The single read of the rollout flag.
 *
 * @returns {boolean} Whether the flag is on.
 */
const useRelationshipsCardIsOn = () => Boolean(useFeatureFlag('rip_sharedImageViewer_relationshipsCard')?.isOn)

/**
 * Whether a relationships section renders at all. Also used by panels that route their stepper past
 * it, so the two cannot disagree.
 *
 * @param {boolean} [householdHasOtherPersons=false] Whether the person's record holds anyone else.
 * @returns {boolean} Whether the section renders.
 */
export const useRelationshipsSectionVisible = (householdHasOtherPersons = false) =>
  useRelationshipsCardIsOn() || householdHasOtherPersons

/**
 * Renders whichever relationships card the rollout flag selects. Every other prop forwards untouched.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.householdHasOtherPersons=false] Gates the card being replaced only.
 * @returns {JSX.Element|null}
 */
const RelationshipsCardSwitch = ({ householdHasOtherPersons = false, ...cardProps }) => {
  const relationshipsCardIsOn = useRelationshipsCardIsOn()
  // Same rule as useRelationshipsSectionVisible, which panels use to route their stepper past this card.
  const sectionVisible = relationshipsCardIsOn || householdHasOtherPersons

  if (!sectionVisible) return null

  // eslint-disable-next-line react/jsx-props-no-spreading
  return relationshipsCardIsOn ? <RelationshipsCard {...cardProps} /> : <FamilyMembersCard {...cardProps} />
}

RelationshipsCardSwitch.propTypes = {
  householdHasOtherPersons: PropTypes.bool,
}

RelationshipsCardSwitch.defaultProps = {
  householdHasOtherPersons: false,
}

export default RelationshipsCardSwitch
